import {
  Children,
  isValidElement,
  type JSXElementConstructor,
  KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useId,
  useMemo,
  useState
} from 'react'

import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Divider, IconButton, Menu, Stack } from '@mui/material'
import { ConsoleTooltip } from '@/components/ConsoleTooltip'
import { useAuthStore } from '@/stores/useAuthStore'

import { ActionMenuContextProvider, ActionMenuContextType } from '../context/ActionMenuContext'
import { MenuItemLike } from '../index.types'

export type ActionsMenuProps<
  Data extends Record<string, unknown>,
  ItemProps extends MenuItemLike = MenuItemLike
> = {
  children:
    | ReactElement<ItemProps, JSXElementConstructor<ItemProps>>
    | ReactElement<ItemProps, JSXElementConstructor<ItemProps>>[]
  row?: Data
  onOpen?: () => void
  onClose?: () => void
  disabled?: boolean
}

export const ACTIONS_MENU_BUTTON_TEST_ID = 'actions-menu-button'

export const ActionsMenu = <
  Data extends Record<string, unknown>,
  ItemProps extends MenuItemLike = MenuItemLike
>({
  children,
  row,
  disabled,
  onClose,
  onOpen
}: ActionsMenuProps<Data, ItemProps>) => {
  const platform = useAuthStore((state) => state.platform)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const id = useId()

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    e?.stopPropagation()

    if (disabled) return

    setAnchorEl(e.currentTarget)
    onOpen?.()
  }

  const handleClose = (e?: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
    e?.stopPropagation()

    if (e?.type === 'keydown' && (e as KeyboardEvent<HTMLElement>)?.code === 'Tab') return

    setAnchorEl(null)
    onClose?.()
  }

  const { menuItems, inlineItems } = useMemo(() => {
    const menuItems: ReactNode[] = []
    const inlineItems: ReactNode[] = []
    const childrenArray = Children.toArray(children).filter((child) =>
      isValidElement(child)
    ) as ReactElement<ItemProps, JSXElementConstructor<ItemProps>>[]
    const filteredChildren = childrenArray.filter((child) => {
      const childPlatform = child.props.platform
      return (
        !childPlatform ||
        (Array.isArray(childPlatform)
          ? childPlatform.includes(platform)
          : childPlatform === platform)
      )
    })

    const numberOfMenuWrappedItems = filteredChildren.filter(
      (child) => !child.props.displaySeparately
    ).length

    filteredChildren.forEach((child, index) => {
      const childProps = child.props
      // It doesnt make sense to put items to the menu if there is only one
      // So we manually set inline to true to the only item in the menu if there is any
      const shouldDisplaySeparately = childProps.displaySeparately ?? numberOfMenuWrappedItems <= 1

      if (shouldDisplaySeparately) {
        inlineItems.push({ ...child, props: { ...childProps, displaySeparately: true } })
      } else {
        const isDividerBefore = childProps?.divider === 'before' && index > 0
        const isDividerAfter =
          childProps?.divider === 'after' && index < filteredChildren.length - 1

        if (isDividerBefore)
          menuItems.push(
            <Divider key={`${child.key}-divider-before`} data-testid="divider-before" />
          )
        menuItems.push(child)
        if (isDividerAfter)
          menuItems.push(<Divider key={`${child.key}-divider-after`} data-testid="divider-after" />)
      }
    })

    return { menuItems, inlineItems }
  }, [children, row, platform])

  const contextValue: ActionMenuContextType<Data> | undefined = useMemo(
    () =>
      row
        ? {
            row,
            closeMenu: handleClose
          }
        : undefined,
    [row, handleClose]
  )

  if (!row || !contextValue) return null

  return (
    <ActionMenuContextProvider value={contextValue}>
      {inlineItems.length > 0 && (
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          {inlineItems}
        </Stack>
      )}

      {menuItems.length > 0 && (
        <>
          <ConsoleTooltip title="Actions" placement="top" arrow>
            <IconButton
              aria-label="more actions"
              id={`${id}-button`}
              aria-controls={open ? `${id}-menu` : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-haspopup="true"
              onClick={handleClick}
              disabled={disabled}
              data-testid={ACTIONS_MENU_BUTTON_TEST_ID}
            >
              <MoreVertIcon />
            </IconButton>
          </ConsoleTooltip>
          <Menu
            id={`${id}-menu`}
            anchorEl={anchorEl}
            open={open}
            onClose={() => handleClose()}
            slotProps={{
              paper: { sx: { boxShadow: 3, borderRadius: 1, mt: 0.5 } },
              list: { 'aria-labelledby': `${id}-button` }
            }}
          >
            {menuItems}
          </Menu>
        </>
      )}
    </ActionMenuContextProvider>
  )
}
